// src/pages/webhook/WebhookListPage.tsx
// Webhook endpoints management — list, create (dialog), enable/disable, delete.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useWebhook, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Alert,
  ErrorAlert,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from '@react/components';
import type { WebhookEndpoint } from '@core/types/webhook';
import {
  getEndpointStatusDisplay,
  formatUrl,
  formatDateTime,
  eventTypeCount,
  canDisable,
  canEnable,
  canDelete,
} from '../../features/webhooks/logic';
import { WebhookEndpointFormDialog } from '../../features/webhooks/WebhookEndpointFormDialog';
import { Badge } from '@react/components';

// --- Inline SVG icons ---

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  );
}

function ChevronIcon({ className, open }: { className?: string; open: boolean }) {
  return (
    <svg
      className={`${className ?? ''} transition-transform ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// --- One-time secret banner (shown after create) ---

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);
  return { copied, copy };
}

function OneTimeSecretBanner({
  secret,
  endpointId,
  onDismiss,
}: {
  secret: string;
  endpointId: string | null;
  onDismiss: () => void;
}) {
  const { t } = useI18n();
  const { copied, copy } = useCopyToClipboard();
  return (
    <Alert variant="success" dismissible onDismiss={onDismiss}>
      <div className="space-y-3">
        <p className="text-sm font-medium">{t('webhooks.secretCreatedTitle')}</p>
        <p className="text-xs text-muted-foreground">{t('webhooks.secretCreatedWarning')}</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 select-all break-all rounded-md bg-muted px-3 py-2 font-mono text-sm text-foreground">
            {secret}
          </code>
          <Button variant="secondary" size="sm" onClick={() => copy(secret)} aria-label={t('common.copy')}>
            {copied ? <CheckIcon className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
          </Button>
        </div>
        {endpointId && (
          <Link to={`/webhooks/${endpointId}`} className="text-xs text-primary underline underline-offset-2">
            {t('webhooks.viewEndpoint')}
          </Link>
        )}
      </div>
    </Alert>
  );
}

// --- Endpoint Row ---

function EndpointRow({ endpoint, onToggle, onDelete, t }: {
  endpoint: WebhookEndpoint;
  onToggle: (id: string, enable: boolean) => void;
  onDelete: (id: string) => void;
  t: (key: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const id = endpoint.entity?.id ?? '';
  const status = getEndpointStatusDisplay(endpoint.status);

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <div className="flex items-center gap-2">
            <ChevronIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" open={expanded} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{endpoint.name}</p>
              {endpoint.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{endpoint.description}</p>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <GlobeIcon className="h-3.5 w-3.5 flex-shrink-0" />
            <code className="text-xs truncate max-w-[200px]">{formatUrl(endpoint.url)}</code>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={status.variant}>{t(status.labelKey)}</Badge>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {eventTypeCount(endpoint)}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {endpoint.last_success_at ? formatDateTime(endpoint.last_success_at as any) : t('webhooks.neverTriggered')}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Link to={`/webhooks/${id}`}>
              <Button variant="ghost" size="sm">{t('common.view')}</Button>
            </Link>
            {canDisable(endpoint) && (
              <Button variant="ghost" size="sm" onClick={() => onToggle(id, false)}>
                {t('webhooks.disable')}
              </Button>
            )}
            {canEnable(endpoint) && (
              <Button variant="ghost" size="sm" onClick={() => onToggle(id, true)}>
                {t('webhooks.enable')}
              </Button>
            )}
            {canDelete(endpoint) && (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(id)}>
                {t('common.delete')}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/20 px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">{t('webhooks.fieldUrl')}</p>
                <code className="text-xs text-foreground break-all">{endpoint.url}</code>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">{t('webhooks.fieldAuthType')}</p>
                <p className="text-xs text-foreground">{endpoint.auth_type?.replace('WEBHOOK_AUTH_TYPE_', '') ?? 'HMAC'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">{t('webhooks.fieldFailures')}</p>
                <p className="text-xs text-foreground">{endpoint.consecutive_failures}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">{t('webhooks.fieldCreated')}</p>
                <p className="text-xs text-foreground">{formatDateTime(endpoint.created_at as any)}</p>
              </div>
              {endpoint.subscribe_all ? (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('webhooks.fieldEvents')}</p>
                  <Badge variant="default">{t('webhooks.allEvents')}</Badge>
                </div>
              ) : endpoint.event_types?.length > 0 ? (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('webhooks.fieldEvents')}</p>
                  <div className="flex flex-wrap gap-1">
                    {endpoint.event_types.map((et) => (
                      <Badge key={et} variant="secondary">{et}</Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// --- Main Page ---

export const WebhookListPage = observer(() => {
  const store = useWebhook();
  const { t } = useI18n();
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const loadEndpoints = useCallback(async () => {
    try {
      const response = await store.ListEndpoints();
      setEndpoints(response.endpoints?.items ?? []);
    } catch {
      // Error handled by store
    }
  }, [store]);

  useEffect(() => {
    loadEndpoints();
  }, [loadEndpoints]);

  const handleToggle = async (id: string, enable: boolean) => {
    try {
      if (enable) {
        await store.EnableEndpoint(id, {});
      } else {
        await store.DisableEndpoint(id, {});
      }
      loadEndpoints();
    } catch {
      // Error handled by store
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('webhooks.deleteConfirm'))) return;
    try {
      await store.DeleteEndpoint(id);
      loadEndpoints();
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('webhooks.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('webhooks.subtitle')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-2" />
          {t('webhooks.createEndpoint')}
        </Button>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {createdSecret && (
        <OneTimeSecretBanner
          secret={createdSecret}
          endpointId={createdId}
          onDismiss={() => {
            setCreatedSecret(null);
            setCreatedId(null);
          }}
        />
      )}

      {/* Endpoints table */}
      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('webhooks.columnName')}</TableHead>
              <TableHead>{t('webhooks.columnUrl')}</TableHead>
              <TableHead>{t('webhooks.columnStatus')}</TableHead>
              <TableHead>{t('webhooks.columnEvents')}</TableHead>
              <TableHead>{t('webhooks.columnLastSuccess')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div className="text-muted-foreground">{t('common.loading')}</div>
                </TableCell>
              </TableRow>
            ) : endpoints.length === 0 ? (
              <TableEmpty colSpan={6} message={t('webhooks.emptyState')} />
            ) : (
              endpoints.map((ep) => (
                <EndpointRow
                  key={ep.entity?.id}
                  endpoint={ep}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                  t={t}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <WebhookEndpointFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(secret, id) => {
          setCreatedSecret(secret);
          setCreatedId(id);
          loadEndpoints();
        }}
      />
    </div>
  );
});

export default WebhookListPage;
